import { useMemo } from 'react';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@savvy-kit/components/dialog';
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from '@savvy-kit/components/empty';
import { Spinner } from '@savvy-kit/components/spinner';
import { Waypoints, XIcon } from 'lucide-react';
import { unmaskFragment } from 'src/gql';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import { buildContactWebGraph } from './build-graph';
import { ContactWebCanvas } from './contact-web-canvas';
import { ContactWebModalQuery, ContactWebTabFragment } from './contact-web-graphql';

type Props = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The raw CenterOfInfluenceRep id to preselect and center the canvas on when it opens. */
  readonly focusRepId?: string;
  /** The raw Household id to preselect and center the canvas on when it opens. */
  readonly focusHouseholdId?: string;
};

export function ContactWebModal({ open, onOpenChange, focusRepId, focusHouseholdId }: Props) {
  const { data, loading } = useQueryWithErrorLogging(ContactWebModalQuery, { skip: !open });

  const graph = useMemo(() => {
    const unmasked = unmaskFragment(ContactWebTabFragment, data);
    return buildContactWebGraph(unmasked?.centerOfInfluenceReps ?? []);
  }, [data]);

  let focusNodeId: string | null = null;
  if (focusRepId !== undefined) {
    focusNodeId = `professional:${focusRepId}`;
  } else if (focusHouseholdId !== undefined) {
    focusNodeId = `household:${focusHouseholdId}`;
  }

  function renderBody() {
    if (loading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Spinner className="size-10 text-primary" />
        </div>
      );
    }
    if (graph.nodes.length === 0) {
      return (
        <Empty>
          <EmptyMedia variant="icon">
            <Waypoints />
          </EmptyMedia>
          <EmptyTitle>No relationships yet</EmptyTitle>
          <EmptyDescription>
            Link a professional contact to a household, or log a referral, to start building your web.
          </EmptyDescription>
        </Empty>
      );
    }
    return <ContactWebCanvas graph={graph} focusNodeId={focusNodeId} showDetailPanel={false} />;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="immersive" showCloseButton={false}>
        <DialogHeader className="flex-row items-center justify-between border-b border-light px-6 py-4">
          <DialogTitle className="text-base font-semibold text-dark">Contact web</DialogTitle>
          <DialogClose className="text-dark">
            <XIcon className="size-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <div className="min-h-0 flex-1">{renderBody()}</div>
      </DialogContent>
    </Dialog>
  );
}
